import React, { useEffect, useState } from 'react';
import { Button, Descriptions, Space, Table, Tag, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useNavigate, useParams } from 'react-router-dom';
import { REGION_OPTIONS } from './packageFormOptions';
import {
  PLAN_PERIOD_LABEL,
  PLAN_PRICE_STATUS_LABEL,
  PLAN_SKU_TYPE_LABEL,
  getPlanDetail,
  type PlanDetailContent,
  type PlanFeatureDescription,
  type PlanFeaturePolicy,
  type PlanSku
} from './packageManageData';

function dash(value?: string | number | null) {
  if (value == null || value === '') return '—';
  return String(value);
}

function periodLabel(period?: string) {
  if (!period) return '—';
  return PLAN_PERIOD_LABEL[period] || period;
}

function formatMinorAmount(amountMinor?: number, fractionDigits?: number) {
  if (amountMinor == null || !Number.isFinite(amountMinor)) return '—';
  const minor = Math.trunc(amountMinor);
  if (!Number.isSafeInteger(minor)) return String(amountMinor);
  const digits = fractionDigits == null || fractionDigits < 0 ? 0 : Math.min(Math.trunc(fractionDigits), 8);
  const sign = minor < 0 ? '-' : '';
  const abs = Math.abs(minor);
  if (digits === 0) return sign + abs.toLocaleString('en-US');
  const factor = 10 ** digits;
  const whole = Math.trunc(abs / factor);
  const fraction = String(abs % factor).padStart(digits, '0');
  return `${sign}${whole.toLocaleString('en-US')}.${fraction}`;
}

function regionText(regionCode?: string) {
  if (!regionCode) return '—';
  const label = REGION_OPTIONS.find((item) => item.value === regionCode)?.label;
  return label ? `${label} ${regionCode}` : regionCode;
}

function featureValue(row: PlanFeatureDescription) {
  if (row.displayType === 'BOOL') return row.supported ? '支持' : '不支持';
  if (row.displayType === 'QUOTA') {
    return `${dash(row.quota)} ${row.unit || ''} / ${periodLabel(row.period)}`.trim();
  }
  if (row.displayType === 'TEXT') return row.text || '—';
  return row.text || '—';
}

const featureColumns: ColumnsType<PlanFeatureDescription> = [
  { title: '名称', dataIndex: 'title', render: (value?: string) => value || '—' },
  { title: '类型', dataIndex: 'displayType', width: 100, render: (value?: string) => value || '—' },
  { title: '内容', key: 'value', render: (_, row) => featureValue(row) }
];

const policyColumns: ColumnsType<PlanFeaturePolicy> = [
  { title: '功能编号', dataIndex: 'featureNo', render: (value?: string) => value || '—' },
  { title: '策略类型', dataIndex: 'policyType', render: (value?: string) => value || '—' },
  { title: '周期', dataIndex: 'period', width: 120, render: (value?: string) => periodLabel(value) },
  {
    title: '上限',
    dataIndex: 'quotaLimit',
    width: 120,
    render: (value?: number) => (value == null ? '—' : value.toLocaleString('en-US'))
  }
];

const priceColumns: ColumnsType<PlanSku> = [
  { title: 'SKU', dataIndex: 'skuCode', render: (value?: string) => value || '—' },
  {
    title: '类型',
    dataIndex: 'skuType',
    width: 120,
    render: (value?: string) => PLAN_SKU_TYPE_LABEL[value ?? ''] || value || '—'
  },
  { title: '地区', dataIndex: 'regionCode', width: 140, render: (value?: string) => regionText(value) },
  {
    title: '价格',
    key: 'amount',
    render: (_, row) =>
      `${formatMinorAmount(row.amountMinor, row.currencyFractionDigits)} ${row.currency || ''}`.trim()
  },
  {
    title: '小数位',
    dataIndex: 'currencyFractionDigits',
    width: 80,
    render: (value?: number) => (value == null ? '—' : value)
  },
  {
    title: '有效天数',
    dataIndex: 'durationDays',
    width: 100,
    render: (value?: number) => (value == null ? '—' : `${value}天`)
  },
  {
    title: '状态',
    dataIndex: 'status',
    width: 90,
    render: (value?: string) => PLAN_PRICE_STATUS_LABEL[value ?? ''] || value || '—'
  }
];

export function PackageDetail() {
  const { planCode = '' } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<PlanDetailContent | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const code = planCode.trim();
    if (!code) return;
    let cancelled = false;
    setLoading(true);
    getPlanDetail(code)
      .then((res) => {
        if (cancelled) return;
        if (res.code !== 200 || !res.content) {
          message.error(res.message || '套餐详情加载失败');
          setDetail(null);
          return;
        }
        setDetail(res.content);
      })
      .catch(() => {
        if (!cancelled) message.error('套餐详情加载失败');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [planCode]);

  const plan = detail?.data?.plan;
  const revision = detail?.data?.currentRevision?.revision;
  const current = detail?.data?.currentRevision;

  return (
    <div className='admin-page-card'>
      <div className='mb-4 flex items-center justify-between gap-3'>
        <h2 className='m-0 text-base font-semibold text-slate-800'>{plan?.displayName || '套餐详情'}</h2>
        <Space>
          <Button
            type='primary'
            disabled={!/^[0-9]{21}$/.test(planCode.trim())}
            onClick={() => navigate(`/devops/creator-outreach/packages/${planCode.trim()}/edit`)}
          >
            编辑
          </Button>
          <Button onClick={() => navigate('/devops/creator-outreach/packages')}>返回</Button>
        </Space>
      </div>

      <Descriptions bordered size='small' column={2} className='mb-4'>
        <Descriptions.Item label='套餐编码'>{dash(plan?.planCode || planCode)}</Descriptions.Item>
        <Descriptions.Item label='套餐等级'>{dash(plan?.grade)}</Descriptions.Item>
        <Descriptions.Item label='配置版本'>{dash(plan?.currentRevisionCode)}</Descriptions.Item>
        <Descriptions.Item label='配置摘要'>{dash(detail?.configHash)}</Descriptions.Item>
        <Descriptions.Item label='展示'>
          {plan?.visible == null ? '—' : plan.visible ? '展示' : '不展示'}
        </Descriptions.Item>
        <Descriptions.Item label='购买'>
          {plan?.purchaseEnabled == null ? '—' : plan.purchaseEnabled ? '开放' : '未开放'}
        </Descriptions.Item>
      </Descriptions>

      <h3 className='mb-2 text-sm font-semibold text-slate-700'>当前版本</h3>
      <Descriptions bordered size='small' column={2} className='mb-4'>
        <Descriptions.Item label='版本编码'>{dash(revision?.revisionCode)}</Descriptions.Item>
        <Descriptions.Item label='版本序号'>{dash(revision?.revisionNo)}</Descriptions.Item>
        <Descriptions.Item label='版本名称' span={2}>
          {dash(revision?.displayName)}
        </Descriptions.Item>
        <Descriptions.Item label='基础店铺'>{dash(revision?.shopBaseLimit)}</Descriptions.Item>
        <Descriptions.Item label='邀约额度'>
          {dash(revision?.inviteBaseLimit)} / {periodLabel(revision?.invitePeriod)}
        </Descriptions.Item>
        <Descriptions.Item label='私信额度'>
          {dash(revision?.dmBaseLimit)} / {periodLabel(revision?.dmPeriod)}
        </Descriptions.Item>
        <Descriptions.Item label='邮件额度'>
          {dash(revision?.mailBaseLimit)} / {periodLabel(revision?.mailPeriod)}
        </Descriptions.Item>
      </Descriptions>

      <h3 className='mb-2 text-sm font-semibold text-slate-700'>功能说明</h3>
      <Table<PlanFeatureDescription>
        className='admin-user-table mb-4'
        size='small'
        rowKey={(row, index) => `${row.title ?? ''}-${index}`}
        columns={featureColumns}
        dataSource={current?.featureDescriptions ?? []}
        loading={loading}
        pagination={false}
        locale={{ emptyText: '暂无功能说明' }}
      />

      <h3 className='mb-2 text-sm font-semibold text-slate-700'>功能限制</h3>
      <Table<PlanFeaturePolicy>
        className='admin-user-table mb-4'
        size='small'
        rowKey={(row, index) => `${row.featureNo ?? ''}-${row.policyType ?? ''}-${index}`}
        columns={policyColumns}
        dataSource={current?.featurePolicies ?? []}
        pagination={false}
        locale={{ emptyText: '暂无功能限制' }}
      />

      <h3 className='mb-2 text-sm font-semibold text-slate-700'>售卖规格</h3>
      <Table<PlanSku>
        className='admin-user-table mb-4'
        size='small'
        rowKey={(row, index) => row.skuCode || `${row.regionCode ?? ''}-${index}`}
        columns={priceColumns}
        dataSource={current?.prices ?? []}
        pagination={false}
        locale={{ emptyText: '暂无付费规格' }}
      />

      <h3 className='mb-2 text-sm font-semibold text-slate-700'>功能组</h3>
      <div className='mb-4 flex flex-wrap gap-2'>
        {(current?.groupCodes ?? []).length === 0 ? (
          <span className='text-sm text-slate-400'>—</span>
        ) : (
          current?.groupCodes?.map((code) => (
            <Tag key={code} className='m-0'>
              {code}
            </Tag>
          ))
        )}
      </div>

      <h3 className='mb-2 text-sm font-semibold text-slate-700'>权限快照</h3>
      <div className='flex flex-wrap gap-2'>
        {(current?.effectiveFeatureCodes ?? []).length === 0 ? (
          <span className='text-sm text-slate-400'>—</span>
        ) : (
          current?.effectiveFeatureCodes?.map((code) => (
            <Tag key={code} className='m-0'>
              {code}
            </Tag>
          ))
        )}
      </div>
    </div>
  );
}
